import { FC, useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router";
import { DEFAULT_ITEMS_PER_PAGE } from "../../../constants/app";
import { STORAGE_KEYS } from "../../../constants/storageKeys";
import { AUTH_ROUTE } from "../../../pages/App/routes";
import {
  useAppDispatch as useDispatch,
  useAppSelector as useSelector,
} from "../../../store";
import {
  hideTransparentPreloaderAction,
  showTransparentPreloaderAction,
} from "../../../store/application/slice";
import { getCandidateDataForCentreThunk } from "../../../store/centers/thunks";
import {
  getActiveVacancies,
  toggleCandidatePreferredStatus,
} from "../../../store/vacancy/thunks";
import {
  AcceptedVacancySortField,
  PendingVacancySortField,
  SortOrder,
  TVacancyDetail,
} from "../../../store/vacancy/types";
import storage from "../../../utils/storage";
import PaginationControls from "../../Pagination/PaginationControls";
import Chevron from "../../UI/Icons/Chevron";
import PopupModal from "../../UI/Popup";
import ViewProfile from "../Candidates/PopupModal/ViewProfile";
import AcceptedCard from "./Accepted/AcceptedCard";
import AcceptedTableHeader from "./Accepted/AcceptedTableHeader";
import PendingCard from "./Pending/PendingCard";
import PendingTableHeader from "./Pending/PendingTableHeader";
import CancelVacancy from "./Popup/CancelVacancy";
import TabsHeader from "./TabsHeader";

type ModalType = "CANDIDATE_PROFILE" | "CANCEL" | null;

const Vacancies: FC = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const [isPendingAccordion, setIsPendingAccordion] = useState(true);
  const [isAcceptedAccordion, setIsAcceptedAccordion] = useState(true);
  const [showProfileModal, setShowProfileModal] = useState<boolean>(false);
  const [pendingDropdownId, setPendingDropdownId] = useState<number | null>(
    null
  );
  const [acceptedDropdownId, setAcceptedDropdownId] = useState<number | null>(
    null
  );
  const [activeModal, setActiveModal] = useState<ModalType>(null);
  const selectedCentreDetails = useSelector(
    (state) => state.vacancy.selectedCentreDetails
  );
  const isShowNotification = useSelector(
    (state) => state.app.isShowNotification
  );
  const {
    pendingVacancies,
    totalPendingVacancies,
    acceptedVacancies,
    totalAcceptedVacancies,
    pendingpagination,
    acceptedpagination,
  } = useSelector((state) => state.vacancy.active);
  // Local states for sorting
  const [pendingSortBy, setPendingSortBy] =
    useState<PendingVacancySortField>("created_at");
  const [pendingSortOrder, setPendingSortOrder] = useState<SortOrder>("desc");
  const [acceptedSortBy, setAcceptedSortBy] =
    useState<AcceptedVacancySortField>("timeframe");
  const [acceptedSortOrder, setAcceptedSortOrder] = useState<SortOrder>("asc");
  // Pagination states
  const [pendingpage, setPendingPage] = useState<number>(1);
  const [pendingPerPage, setPendingPerPage] = useState<number>(
    DEFAULT_ITEMS_PER_PAGE
  );
  const [acceptedPage, setAcceptedPage] = useState<number>(1);
  const [acceptedPerPage, setAcceptedPerPage] = useState<number>(
    DEFAULT_ITEMS_PER_PAGE
  );

  const [selectedVacancy, setSelectedVacancy] = useState<TVacancyDetail | null>(
    null
  );

  const onClickClose = () => {
    setActiveModal(null);
    setSelectedVacancy(null);
    setShowProfileModal(false);
  };

  const handleCancel = (vacancy: TVacancyDetail) => {
    setPendingDropdownId(null);
    setAcceptedDropdownId(null);
    setSelectedVacancy(vacancy);
    setActiveModal("CANCEL");
  };

  const handleViewVacancy = async (vacancy: TVacancyDetail) => {
    storage.set(STORAGE_KEYS.SELECTED_VACANCY_ID, vacancy.uuid);
    navigate(AUTH_ROUTE.PENDING_VACANCY);
  };

  const handleEditVacancy = (vacancy: TVacancyDetail) => {
    storage.set(STORAGE_KEYS.SELECTED_VACANCY_ID, vacancy.uuid);
    navigate(AUTH_ROUTE.EDIT_VACANCY);
  };

  const handleDuplicateVacancy = (vacancy: TVacancyDetail) => {
    storage.set(STORAGE_KEYS.DUPLICATE_VACANCY_ID, vacancy.uuid);
    navigate(AUTH_ROUTE.CREATE_VACANCY);
  };

  const handleCandidatePreferred = (vacancy: TVacancyDetail) => {
    dispatch(
      toggleCandidatePreferredStatus({
        centre_id: vacancy.centre_id,
        centre_uuid: vacancy.centre.uuid,
        user_id: vacancy.selected_candidate!.id,
        user_uuid: vacancy.selected_candidate!.uuid,
        action: vacancy.selected_candidate.is_preferred_candidate
          ? "remove"
          : "mark",
        is_preferred: vacancy.selected_candidate.is_preferred_candidate
          ? false
          : true,
      })
    );
  };

  // Handle sorting logic
  const handlePendingSortChange = (field: PendingVacancySortField) => {
    if (pendingSortBy === field) {
      setPendingSortOrder((prev) => (prev === "asc" ? "desc" : "asc"));
    } else {
      setPendingSortBy(field);
      setPendingSortOrder("asc");
    }
  };

  const handleAcceptedSortChange = (field: AcceptedVacancySortField) => {
    if (acceptedSortBy === field) {
      setAcceptedSortOrder((prev) => (prev === "asc" ? "desc" : "asc"));
    } else {
      setAcceptedSortBy(field);
      setAcceptedSortOrder("asc");
    }
  };

  // Handle pagination link click for pending vacancies
  const handlePendingPageChange = (url: string | null) => {
    if (url) {
      const page = new URL(url).searchParams.get("pending_vacancy_page");
      setPendingPage(Number(page));
    }
  };

  // Handle pagination link click for accepted vacancies
  const handleAcceptedPageChange = (url: string | null) => {
    if (url) {
      const page = new URL(url).searchParams.get("accepted_vacancy_page");
      setAcceptedPage(Number(page));
    }
  };

  // Fetch active vacancies (debounced, sorted, paginated)
  const fetchActiveVacancies = useCallback(async () => {
    dispatch(showTransparentPreloaderAction());
    dispatch(
      getActiveVacancies({
        pending_sort_by: pendingSortBy,
        pending_sort_order: pendingSortOrder,
        pending_vacancy_page: pendingpage,
        pending_per_page: pendingPerPage,

        accepted_sort_by: acceptedSortBy,
        accepted_sort_order: acceptedSortOrder,
        accepted_vacancy_page: acceptedPage,
        accepted_per_page: acceptedPerPage,
        centre_uuid:
          selectedCentreDetails !== null
            ? selectedCentreDetails
            : storage.get(STORAGE_KEYS.SELECTED_CENTRE_UUID),
      })
    ).finally(() => {
      dispatch(hideTransparentPreloaderAction());
    });
  }, [
    dispatch,
    pendingpage,
    pendingPerPage,
    pendingSortBy,
    pendingSortOrder,
    acceptedPage,
    acceptedPerPage,
    acceptedSortBy,
    acceptedSortOrder,
    selectedCentreDetails,
  ]);

  // Call fetch function on filter/pagination/sort change
  useEffect(() => {
    if(!isShowNotification) {
      fetchActiveVacancies();
    }
  }, [
    fetchActiveVacancies,
    pendingpage,
    pendingPerPage,
    pendingSortBy,
    pendingSortOrder,
    acceptedPage,
    acceptedPerPage,
    acceptedSortBy,
    acceptedSortOrder,
    isShowNotification
  ]);

  const handleCandidateProfileView = async (vacancy: TVacancyDetail) => {
    dispatch(showTransparentPreloaderAction());
    await dispatch(
      getCandidateDataForCentreThunk({
        candidate_id: String(vacancy.selected_candidate.uuid),
        centre_id: String(vacancy.centre_id),
      })
    );
    dispatch(hideTransparentPreloaderAction());
    setShowProfileModal(!false);
  };

  return (
    <>
      <div className="d-flex flex-column gap-20">
        <TabsHeader activeHeader={"Active"} />
        <div className="d-flex flex-column">
          <div
            className="bg-off-white py-8px px-15px radius-8px d-flex gap-8 align-items-center cursor-pointer"
            onClick={() => setIsPendingAccordion(!isPendingAccordion)}
          >
            <Chevron isOpen={isPendingAccordion} />
            <div className="fs-16 fw-700 text-black">
              {totalPendingVacancies} Pending
            </div>
          </div>
          {isPendingAccordion && (
            <>
              <PendingTableHeader
                sortBy={pendingSortBy}
                sortOrder={pendingSortOrder}
                onSortChange={handlePendingSortChange}
              />
              {pendingVacancies.length > 0 ? (
                pendingVacancies.map((vacancy: TVacancyDetail) => (
                  <PendingCard
                    key={vacancy.id}
                    vacancy={vacancy}
                    showDropdown={pendingDropdownId === vacancy.id}
                    setShowDropdown={(val) =>
                      setPendingDropdownId(val ? vacancy.id : null)
                    }
                    onViewDetails={() => handleViewVacancy(vacancy)}
                    handleEdit={() => handleEditVacancy(vacancy)}
                    handleDuplicate={() => handleDuplicateVacancy(vacancy)}
                    handleCancel={() => handleCancel(vacancy)}
                  />
                ))
              ) : (
                <div className="d-flex gap-20 align-items-center justify-content-center text-center p-15px">
                  <div className="fs-16 fw-400 text-grey-medium">
                    No pending vacancies found
                  </div>
                </div>
              )}
              {/* Render pagination controls if there are more than 10 pending vacancies */}
              {pendingpagination &&
                pendingpagination.total > DEFAULT_ITEMS_PER_PAGE && (
                  <PaginationControls
                    currentPage={pendingpage} // Current page number
                    totalPages={pendingpagination?.last_page} // Total number of pages
                    paginationLinks={pendingpagination?.links} // List of pagination links
                    onPageChange={handlePendingPageChange} // Function to handle page change
                    setPerPageList={setPendingPerPage} // Function to set the number of items per page
                    setCurrentPage={setPendingPage} // Function to set the current page
                  />
                )}
            </>
          )}
        </div>
        <div className="d-flex flex-column">
          <div
            className="bg-teal-light py-8px px-15px radius-8px d-flex gap-8 align-items-center cursor-pointer"
            onClick={() => setIsAcceptedAccordion(!isAcceptedAccordion)}
          >
            <Chevron isOpen={isAcceptedAccordion} />
            <div className="fs-16 fw-700 text-black">
              {totalAcceptedVacancies} Accepted
            </div>
          </div>
          {isAcceptedAccordion && (
            <>
              <AcceptedTableHeader
                sortBy={acceptedSortBy}
                sortOrder={acceptedSortOrder}
                onSortChange={handleAcceptedSortChange}
              />
              {acceptedVacancies.length > 0 ? (
                acceptedVacancies.map((vacancy) => (
                  <AcceptedCard
                    key={vacancy.id}
                    vacancy={vacancy}
                    showDropdown={acceptedDropdownId === vacancy.id}
                    setShowDropdown={(val) =>
                      setAcceptedDropdownId(val ? vacancy.id : null)
                    }
                    onViewDetails={() => handleViewVacancy(vacancy)}
                    handleProfileView={() =>
                      handleCandidateProfileView(vacancy)
                    }
                    handlePreferred={() => handleCandidatePreferred(vacancy)}
                    handleDuplicate={() => handleDuplicateVacancy(vacancy)}
                    handleCancel={() => handleCancel(vacancy)}
                  />
                ))
              ) : (
                <div className="d-flex gap-20 align-items-center justify-content-center text-center p-15px">
                  <div className="fs-16 fw-400 text-grey-medium">
                    No accepted vacancies found
                  </div>
                </div>
              )}
              {/* Render pagination controls if there are more than 10 accepeted vacancies */}
              {acceptedpagination &&
                acceptedpagination?.total > DEFAULT_ITEMS_PER_PAGE && (
                  <PaginationControls
                    currentPage={acceptedPage} // Current page number
                    totalPages={acceptedpagination?.last_page} // Total number of pages
                    paginationLinks={acceptedpagination?.links} // List of pagination links
                    onPageChange={handleAcceptedPageChange} // Function to handle page change
                    setPerPageList={setAcceptedPerPage} // Function to set the number of items per page
                    setCurrentPage={setAcceptedPage} // Function to set the current page
                  />
                )}
            </>
          )}
        </div>
      </div>
      {/* Candidate Profile Modal */}
      <ViewProfile
        showProfileModal={showProfileModal}
        onClickClose={onClickClose}
      />
      {/* Cancel Modal */}
      <PopupModal
        visible={!!activeModal}
        showCloseBtn={true}
        onClickClose={onClickClose}
        customWidth={650}
      >
        {/* Cancel Modal */}
        {activeModal === "CANCEL" && selectedVacancy && (
          <CancelVacancy vacancy={selectedVacancy} onClose={onClickClose} />
        )}
      </PopupModal>
      {/* Cancel Modal */}
    </>
  );
};

export default Vacancies;
